import type {
    Integration as APIIntegration,
    IntegrationBlock,
    IntegrationsBlocksSet,
} from '@gitbook/api-client';
import { isUrlMatching } from '@gitbook/util-url';

import type { Integration } from './integrations';

export type { IntegrationsBlocksSet };

/**
 * Lookup a block using a filter.
 */
function lookupIntegrationBlockInSet(
    set: IntegrationsBlocksSet,
    filter: (input: { integrationKey: string; block: IntegrationBlock }) => boolean
): undefined | { integrationKey: string; block: IntegrationBlock } {
    for (const integrationKey of Object.keys(set)) {
        for (const block of set[integrationKey]) {
            const entry = { integrationKey, block };
            if (filter(entry)) {
                return entry;
            }
        }
    }
    return undefined;
}

/**
 * Get a specific integration block in a set.
 */
export function getIntegrationBlockInSet(
    set: IntegrationsBlocksSet,
    integrationKey: string,
    blockKey: string
): undefined | IntegrationBlock {
    return lookupIntegrationBlockInSet(set, (entry) => {
        return entry.integrationKey === integrationKey && entry.block.id === blockKey;
    })?.block;
}

/**
 * Create a list of integrations into a set of blocks.
 */
export function createIntegrationsBlocksSet(
    integrations: Array<Integration | APIIntegration | null>
): IntegrationsBlocksSet {
    const integrationBlocks: IntegrationsBlocksSet = {};

    integrations.forEach((integration) => {
        if (integration) {
            integrationBlocks[integration.name] = (integration.blocks || []).map((block) => {
                return {
                    ...block,
                    icon:
                        block.icon ||
                        ('urls' in integration ? integration.urls.icon : integration.iconURL),
                };
            });
        }
    });

    return integrationBlocks;
}

/**
 * Get a block matching an url.
 */
export function getIntegrationBlockForURL(
    integrations: IntegrationsBlocksSet,
    url: string
): undefined | { integrationKey: string; block: IntegrationBlock } {
    return lookupIntegrationBlockInSet(integrations, ({ block }) => {
        const urlUnfurl = block.urlUnfurl || [];
        return urlUnfurl.some((unfurl) => isUrlMatching(url, unfurl));
    });
}
